import { createFileRoute, Link } from '@tanstack/react-router';
import { Bell, Download, Edit3 } from 'lucide-react';
import { downloadCsv } from '@/lib/utils/csv';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { Button } from '@/components/ui/button';
import {
  ESTIMATED_AT_FALLBACK_RATE,
  formatCents,
  useAiCostSummaryQuery,
  useAiModelQuery,
  workClassName,
} from '@/lib/api/ai-costs';
import { AiCostMeter } from '@/components/settings/ai-cost-meter';
import { FirmAiBudgetCard } from '@/components/settings/firm-ai-budget-card';
import { ModelPinningTable } from '@/components/settings/model-pinning-table';
import { SpendByEngagement } from '@/components/settings/spend-by-engagement';
import { WorkClassDonut } from '@/components/settings/work-class-donut';
import { DataGrid, type DataGridColumn } from '@/components/ui/data-grid';
import { cn } from '@/lib/utils/cn';
import { formatDateTime } from '@/lib/utils/format';

/**
 * AI cost governance — `/settings/ai-costs` (T-0112 / C20-122, T-0014).
 *
 * Per `design_handoff_caliber_v2/README.md` §4 this is one of the two
 * **load-bearing** firm-settings screens (the README bolds "AI cost
 * governance"). Mirrors the prototype's `screens-soft-gaps.js →
 * firmAiCosts()` layout:
 *
 *   - Top: firm-cap meter + 12-month trend bars + model-pin summary.
 *   - Middle: per-engagement spend table with burn bars.
 *   - Bottom: work-class breakdown (donut) + recent expensive calls log.
 *
 * Backend gap (flagged in `ai-costs.ts`): no composite endpoint exists
 * today. The screen reads deterministic mock data that mirrors the
 * prototype's `COSTS` block. When the backend lands (likely
 * `GET /api/v1/admin/ai-costs/summary` aggregating across every
 * engagement's `ai_spend_ledger`), swap the call site in
 * `lib/api/ai-costs.ts → getAiCostSummary`.
 */
export const Route = createFileRoute('/settings/ai-costs')({
  component: AiCostsSettings,
});

function AiCostsSettings() {
  const summaryQuery = useAiCostSummaryQuery();
  // The deployments behind the figures; an older server without the
  // endpoint leaves the card saying so instead of failing the page.
  const modelQuery = useAiModelQuery();

  if (summaryQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-body-sm text-ink-500">
        <Spinner /> Loading cost summary…
      </div>
    );
  }
  if (summaryQuery.error) {
    return <ErrorMessage error={summaryQuery.error} />;
  }
  if (!summaryQuery.data) {
    return <p className="text-body-sm text-ink-500">No cost data available.</p>;
  }

  const summary = summaryQuery.data;
  const totalCalls = summary.per_work_class.reduce((acc, w) => acc + w.calls, 0);

  // 12-month trend bars — local since this is a tiny visualisation, not
  // a reusable primitive. Highlights the current (last) month in amber;
  // every other month uses ink-300.
  const maxTrendCents = Math.max(...summary.monthly_trend_cents, 1);

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="t-h1 font-semibold text-ink-900">AI cost governance</h1>
          <p className="t-meta text-ink-500">
            {totalCalls.toLocaleString()} calls month-to-date · {summary.per_work_class.length} work
            classes · model <span className="font-mono">{modelQuery.data?.chat_model ?? '—'}</span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() =>
              downloadCsv(
                'ai-usage.csv',
                Object.keys(summary.per_work_class[0] ?? { work_class: '' }),
                summary.per_work_class.map((w) => Object.values(w) as (string | number)[]),
              )
            }
          >
            <Download className="size-3.5" aria-hidden="true" /> Export usage report
          </Button>
          <Button asChild size="sm" variant="outline">
            <Link to="/settings/notifications">
              <Bell className="size-3.5" aria-hidden="true" /> Alert rules
            </Link>
          </Button>
        </div>
      </header>

      <div className="grid gap-4 lg:grid-cols-[2fr_1fr_1fr]">
        <AiCostMeter summary={summary} />

        <section
          aria-labelledby="ai-cost-trend-heading"
          className="rounded-card border border-ink-100 bg-paper-0 p-card-pad"
        >
          <h2 id="ai-cost-trend-heading" className="text-h3 font-semibold text-ink-900">
            12-month spend
          </h2>
          <div
            aria-hidden="true"
            className="mt-3 flex h-16 items-end gap-1.5 border-b border-dashed border-ink-200 pb-0.5"
          >
            {summary.monthly_trend_cents.map((v, i) => {
              const h = Math.max(4, (v / maxTrendCents) * 56);
              const last = i === summary.monthly_trend_cents.length - 1;
              return (
                <div
                  key={i}
                  className={cn('flex-1 rounded-sm', last ? 'bg-accent-amber-600' : 'bg-ink-300')}
                  style={{ height: h }}
                  title={formatCents(v)}
                />
              );
            })}
          </div>
          <div className="mt-2 flex items-center justify-between font-mono text-meta text-ink-500">
            <span>{relativeMonth(-(summary.monthly_trend_cents.length - 1))}</span>
            <span>{relativeMonth(0)}</span>
          </div>
          <p className="mt-2 text-meta text-ink-500">
            Steady +6.4% MoM · driven by chat &amp; routing
          </p>
        </section>

        <ModelPinningTable summary={summary} model={modelQuery.data ?? null} />
      </div>

      {summary.firm_scope ? <FirmAiBudgetCard firmScope={summary.firm_scope} /> : null}

      <SpendByEngagement summary={summary} />

      <div className="grid gap-4 lg:grid-cols-[1fr_2fr]">
        <section
          aria-labelledby="work-class-donut-heading"
          className="rounded-card border border-ink-100 bg-paper-0 p-card-pad"
        >
          <h2 id="work-class-donut-heading" className="text-h3 font-semibold text-ink-900">
            Work-class breakdown
          </h2>
          <p className="text-meta text-ink-500">Share of MTD spend by work class.</p>
          <div className="mt-3">
            <WorkClassDonut summary={summary} />
          </div>
        </section>

        <RecentExpensiveCalls summary={summary} />
      </div>

      <div className="rounded-card border border-ai-surface-border bg-ai-surface p-3 text-body-sm text-ai-ink">
        <strong className="font-semibold">How caps work.</strong> When an engagement reaches its
        monthly cap, AI-initiated proposals are paused (humans can still chat &amp; trigger re-evals
        manually with partner approval). Firm-level <strong>hard ceiling</strong> at 115%
        auto-throttles every namespace; an audit-trail event is written and Methodology Admin + the
        engagement partner are paged.
      </div>
    </div>
  );
}

interface RecentExpensiveCallsProps {
  summary: ReturnType<typeof useAiCostSummaryQuery> extends { data?: infer S }
    ? NonNullable<S>
    : never;
}

function RecentExpensiveCalls({ summary }: RecentExpensiveCallsProps) {
  const columns: DataGridColumn<(typeof summary.recent_expensive_calls)[number]>[] = [
    {
      key: 'time',
      header: 'Time',
      cell: (r) => <span className="font-mono text-meta">{formatDateTime(r.ts)}</span>,
    },
    {
      key: 'engagement',
      header: 'Eng.',
      cell: (r) => <span>{r.client_name}</span>,
    },
    {
      key: 'work_class',
      header: 'Class',
      cell: (r) => {
        const wc = summary.per_work_class.find((w) => w.id === r.work_class);
        return (
          <span className="inline-flex items-center rounded-chip border border-ai-surface-border bg-ai-surface px-1.5 py-0.5 text-meta font-medium text-ai-ink">
            {wc?.name ?? workClassName(r.work_class)}
          </span>
        );
      },
    },
    {
      key: 'tokens',
      header: 'Tokens',
      headerClassName: 'text-right',
      cellClassName: 'text-right font-mono',
      cell: (r) => `${(r.tokens / 1000).toFixed(1)}k`,
    },
    {
      key: 'cost',
      header: 'Cost',
      headerClassName: 'text-right',
      cellClassName: 'text-right font-mono font-medium text-ink-900',
      cell: (r) =>
        r.estimated ? (
          <span title={`${r.model_id}: ${ESTIMATED_AT_FALLBACK_RATE}`}>
            {formatCents(r.cost_cents)}{' '}
            <span className="font-sans text-meta font-normal text-ink-500">estimated</span>
          </span>
        ) : (
          formatCents(r.cost_cents)
        ),
    },
    {
      key: 'ref',
      header: 'Caller / record',
      cell: (r) => <span className="text-ink-900">{r.ref}</span>,
    },
    {
      key: 'user',
      header: 'User',
      cellClassName: 'text-ink-500',
      cell: (r) => r.user,
    },
  ];
  return (
    <section
      aria-labelledby="recent-expensive-calls-heading"
      className="overflow-hidden rounded-card border border-ink-100 bg-paper-0"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-100 px-card-pad py-3">
        <div>
          <h2 id="recent-expensive-calls-heading" className="text-h3 font-semibold text-ink-900">
            Recent expensive calls
          </h2>
          <p className="text-meta text-ink-500">
            Top {summary.recent_expensive_calls.length} by cost · linked to caller
          </p>
        </div>
      </div>
      <DataGrid
        columns={columns}
        rows={summary.recent_expensive_calls}
        rowKey={(r) => `${r.ts}-${r.engagement_id}`}
      />
    </section>
  );
}

/** Format a month label N months relative to "today". -11 → Jun '25 etc. */
function relativeMonth(offset: number): string {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return d.toLocaleString(undefined, { month: 'short', year: '2-digit' });
}
